import React, { useState, useMemo, useEffect } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Download01Icon,
  FilterIcon,
  Calendar01Icon,
  Search01Icon,
  Tick01Icon,
  Cancel01Icon,
  Alert01Icon,
  HourglassIcon,
  BankIcon
} from '@hugeicons/core-free-icons';
import CommandPalette from '../components/CommandPalette';
import Button from '../components/Button';
import ExportSuccessModal from '../components/ExportSuccessModal';
import DateRangeModal from '../components/DateRangeModal';
import ResponsiveModal from '../components/ResponsiveModal';
import { exportToPDF, exportToCSV } from '../utils/export';
import { api } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { FadeLoader } from 'react-spinners';
import { useAuth } from '../contexts/AuthContext';

const History = () => {
  const { user } = useAuth();
  const [filterType, setFilterType] = useState('all');
  const [dateFilter, setDateFilter] = useState('30_days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [isCmdkOpen, setIsCmdkOpen] = useState(false);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [exportSuccessType, setExportSuccessType] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchHistory = async (isBackground = false) => {
      if (!isBackground) setLoading(true);
      try {
        const endpoint = user?.role === 'agent' 
          ? `/agent/transactions?page=${page}&per_page=15` 
          : `/client/transactions?page=${page}&per_page=15`;

        const response = await api.get(endpoint);
        const data = response.data?.data || response.data || {};
        
        const txList = data.transactions || data.data || [];
        const meta = data.pagination || data.meta || { current_page: 1, last_page: 1, total: txList.length };
        
        // Normalize
        const normalized = txList.map(tx => {
          const rate = parseFloat(tx.order?.current_rate || 1);
          const amountNgn = parseFloat(tx.amount_sent || 0);
          
          console.log(`DEBUG TX ${tx.reference}:`, {
            order_type: tx.order?.transaction_type,
            cashback_rate: tx.order?.cashback_rate,
            spread: tx.order?.spread
          });

          // Calculate cashback if applicable
          let cashbackEarned = 0;
          if (tx.order?.transaction_type === 'deposit' && parseFloat(tx.order?.cashback_rate || 0) > 0) {
            const amountUsd = amountNgn / rate;
            cashbackEarned = amountUsd * parseFloat(tx.order.spread || 0) * parseFloat(tx.order.cashback_rate);
          }

          return {
            id: tx.uuid || tx.uid,
            reference: tx.reference,
            link_token: tx.reference,
            created_at: tx.created_at,
            transaction_type: tx.type === 'payment_confirmation' ? (tx.order?.transaction_type || 'deposit') : tx.type,
            merchant: user?.role === 'agent' ? (tx.client || { name: 'Unknown Client' }) : (tx.order?.merchant || tx.merchant || { name: 'Unknown Merchant' }),
            amount_usd: amountNgn / rate,
            amount_sent: amountNgn,
            currency: tx.currency || 'NGN',
            status: tx.status,
            cashback_earned: cashbackEarned,
            bank_account: tx.bank_account || null
          };
        });

        setTransactions(normalized);
        setPagination(meta);
      } catch (err) {
        console.warn('Failed to fetch history:', err);
        if (!isBackground) setTransactions([]);
      } finally {
        if (!isBackground) setLoading(false);
      }
    };

    if (user) {
      fetchHistory();
      const interval = setInterval(() => fetchHistory(true), 15000); // Poll every 15s
      return () => clearInterval(interval);
    }
  }, [page, user]);

  const handleDateFilterChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setIsDateModalOpen(true);
    } else {
      setDateFilter(value);
      setCustomStartDate('');
      setCustomEndDate('');
    }
  };

  const handleCustomDateApply = (start, end) => {
    setCustomStartDate(start);
    setCustomEndDate(end);
    setDateFilter('custom');
  };

  const filteredTransactions = useMemo(() => {
    let data = transactions;

    // Filter by Type
    if (filterType !== 'all') {
      data = data.filter(tx => tx.transaction_type.toLowerCase() === filterType.toLowerCase());
    }

    // Filter by Date
    const now = new Date(); 
    
    if (dateFilter === 'custom' && customStartDate && customEndDate) {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      data = data.filter(tx => {
        const txDate = new Date(tx.created_at);
        return txDate >= start && txDate <= end;
      });
    } else if (dateFilter !== 'custom') {
      const days = dateFilter === '30_days' ? 30 : dateFilter === '3_months' ? 90 : 365;
      const cutoff = new Date(now);
      cutoff.setDate(cutoff.getDate() - days);
      
      data = data.filter(tx => new Date(tx.created_at) >= cutoff);
    }

    return data;
  }, [transactions, filterType, dateFilter, customStartDate, customEndDate]);
  
  const handleExport = (type) => {
    if (type === 'pdf') {
      const headers = ["Date", "Order ID", "Type", "Counterparty", "Bank", "USD", "NGN", "Status"];
      const mapper = (tx) => [
        new Date(tx.created_at).toLocaleDateString(),
        tx.link_token || tx.id,
        tx.transaction_type,
        tx.merchant?.name || tx.agent?.email || 'N/A',
        tx.bank_account?.bank_name || 'N/A',
        `$${parseFloat(tx.amount_usd || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`,
        `₦${parseFloat(tx.amount_sent || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`,
        tx.status
      ];
      const userName = user?.name || user?.email || 'Valued Client';
      exportToPDF(filteredTransactions, 'Transaction History', headers, mapper, userName);
    } else {
      const flatData = filteredTransactions.map(tx => ({
        date: new Date(tx.created_at).toLocaleDateString(),
        order_id: tx.link_token || tx.id,
        type: tx.transaction_type,
        counterparty: tx.merchant?.name || tx.agent?.email || 'N/A',
        bank: tx.bank_account?.bank_name || 'N/A',
        amount_usd: parseFloat(tx.amount_usd || 0).toFixed(2),
        amount_ngn: parseFloat(tx.amount_sent || 0).toFixed(2),
        status: tx.status
      }));
      exportToCSV(flatData);
    }
    setExportSuccessType(type);
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'completed': 
      case 'verified': return 'bg-green-50 text-soft-green dark:bg-green-900/20 dark:text-green-400';
      case 'cancelled': 
      case 'rejected':
      case 'failed': return 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400';
      case 'disputed': return 'bg-orange-50 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400';
      default: return 'bg-yellow-50 text-yellow-600 dark:bg-yellow-900/20 dark:text-yellow-400'; // pending
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': 
      case 'verified': return Tick01Icon;
      case 'cancelled': 
      case 'rejected':
      case 'failed': return Cancel01Icon;
      case 'disputed': return Alert01Icon;
      default: return HourglassIcon; // pending
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <FadeLoader color="#4ade80" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      <CommandPalette 
        isOpen={isCmdkOpen} 
        setIsOpen={setIsCmdkOpen} 
        transactions={filteredTransactions} 
      />
      <ExportSuccessModal 
        isOpen={!!exportSuccessType}
        onClose={() => setExportSuccessType(null)}
        type={exportSuccessType}
      />
      <DateRangeModal
        isOpen={isDateModalOpen}
        onClose={() => setIsDateModalOpen(false)}
        onApply={handleCustomDateApply}
      />

      <ResponsiveModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        title="Filter History"
      >
        <div className="py-6 space-y-6">
          <div className="space-y-2">
            <label className="text-xs font-black text-muted-gray uppercase tracking-widest">Date Range</label>
            <select 
              value={dateFilter}
              onChange={handleDateFilterChange}
              className="w-full border border-light-gray/20 dark:border-gray-700/50 rounded-2xl px-4 py-3.5 text-sm bg-off-white/50 dark:bg-gray-800/50 text-charcoal dark:text-gray-200 outline-none focus:ring-2 focus:ring-soft-green/20 focus:border-soft-green transition-all"
            >
              <option value="30_days">Last 30 Days</option>
              <option value="3_months">Last 3 Months</option>
              <option value="1_year">This Year</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-black text-muted-gray uppercase tracking-widest">Transaction Type</label>
            <div className="grid grid-cols-3 gap-2">
              {['all', 'deposit', 'withdrawal'].map(type => (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={`py-3 rounded-2xl text-xs font-black capitalize transition-all border ${
                    filterType === type 
                      ? 'bg-soft-green text-white border-soft-green' 
                      : 'bg-off-white dark:bg-gray-800 text-muted-gray border-light-gray/20 dark:border-gray-700/20'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          <Button onClick={() => setIsFilterModalOpen(false)} className="w-full py-4 mt-4 font-black">Apply Filters</Button>
        </div>
      </ResponsiveModal>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-charcoal dark:text-white">Transaction History</h1>
          <p className="text-muted-gray mt-1">Review and export your activity records.</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button variant="secondary" className="gap-2 px-6 py-3 rounded-2xl font-black text-xs sm:text-sm" onClick={() => handleExport('pdf')}>
            <HugeiconsIcon icon={Download01Icon} size={18} />
            Export PDF
          </Button>
          <Button variant="primary" className="gap-2 px-6 py-3 rounded-2xl font-black text-xs sm:text-sm" onClick={() => handleExport('csv')}>
            <HugeiconsIcon icon={Download01Icon} size={18} />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white dark:bg-dark-surface p-4 rounded-2xl border border-light-gray dark:border-dark-border flex items-center gap-4">
        <div 
          className="relative flex-1 min-w-[200px] cursor-pointer"
          onClick={() => setIsCmdkOpen(true)}
        >
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-gray">
            <HugeiconsIcon icon={Search01Icon} size={18} />
          </div>
          <input
            type="text"
            readOnly
            className="block w-full pl-10 pr-12 py-2 border border-light-gray dark:border-gray-700 rounded-xl focus:ring-soft-green focus:border-soft-green bg-off-white/30 dark:bg-gray-800 text-sm cursor-pointer"
            placeholder="Search Order ID or Agent..."
          />
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <span className="text-xs text-muted-gray border border-gray-200 dark:border-gray-600 rounded px-1.5 py-0.5">⌘K</span>
          </div>
        </div>

        {/* Desktop Filters */}
        <div className="hidden lg:flex items-center gap-4">
          <div className="flex items-center gap-2">
            <HugeiconsIcon icon={Calendar01Icon} size={18} className="text-muted-gray" />
            <select 
              value={dateFilter}
              onChange={handleDateFilterChange}
              className="border border-light-gray dark:border-gray-700 rounded-xl px-3 py-2 text-sm bg-white dark:bg-dark-surface text-charcoal dark:text-gray-200 focus:outline-none focus:border-soft-green"
            >
              <option value="30_days">Last 30 Days</option>
              <option value="3_months">Last 3 Months</option>
              <option value="1_year">This Year</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <HugeiconsIcon icon={FilterIcon} size={18} className="text-muted-gray" />
            <select 
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="border border-light-gray dark:border-gray-700 rounded-xl px-3 py-2 text-sm bg-white dark:bg-dark-surface text-charcoal dark:text-gray-200 focus:outline-none focus:border-soft-green"
            >
              <option value="all">All Types</option>
              <option value="deposit">Deposits</option>
              <option value="withdrawal">Withdrawals</option>
            </select>
          </div>
        </div>

        {/* Mobile Filter Toggle */}
        <button 
          onClick={() => setIsFilterModalOpen(true)}
          className="lg:hidden p-2.5 bg-off-white dark:bg-gray-800 border border-light-gray/20 dark:border-gray-700/20 rounded-xl text-muted-gray hover:text-soft-green transition-colors"
        >
          <HugeiconsIcon icon={FilterIcon} size={20} />
        </button>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-dark-surface rounded-2xl border border-light-gray dark:border-dark-border overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-off-white/50 dark:bg-gray-800/50 text-muted-gray text-xs uppercase tracking-wider">
              <tr>
                <th className="px-6 py-4 font-bold">Date</th>
                <th className="px-6 py-4 font-bold">Order ID</th>
                <th className="px-6 py-4 font-bold">Type</th>
                <th className="px-6 py-4 font-bold">Counterparty</th>
                <th className="px-6 py-4 font-bold">Bank</th>
                <th className="px-6 py-4 font-bold text-right">USD</th>
                <th className="px-6 py-4 font-bold text-right">NGN</th>
                <th className="px-6 py-4 font-bold text-right">Cashback</th>
                <th className="px-6 py-4 font-bold text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-light-gray dark:divide-dark-border">
              {filteredTransactions.map((tx) => {
                const Icon = getStatusIcon(tx.status);
                return (
                  <tr 
                    key={tx.link_token || tx.id} 
                    onClick={() => navigate(`/transaction/${tx.id}`)}
                    className="hover:bg-off-white dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-4 text-sm text-charcoal dark:text-gray-200 font-medium">
                      {new Date(tx.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-muted-gray">{tx.link_token}</td>
                    <td className="px-6 py-4">
                      <span className={`text-[10px] px-2 py-1 rounded-md font-bold uppercase tracking-wide ${
                        tx.transaction_type?.includes('deposit') ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400' : 'bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400'
                      }`}>
                        {tx.transaction_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-charcoal dark:text-gray-200">{tx.merchant?.name || 'N/A'}</td>
                    <td className="px-6 py-4">
                      {tx.bank_account ? (
                        <div className="flex items-center gap-2">
                          <BankIcon size={14} className="text-soft-green" />
                          <span className="text-xs font-medium text-charcoal dark:text-gray-200 truncate max-w-[150px]">
                            {tx.bank_account.bank_name}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-gray">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-bold text-charcoal dark:text-gray-200">
                      ${parseFloat(tx.amount_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-bold text-soft-green">
                      ₦{parseFloat(tx.amount_sent || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {tx.cashback_earned > 0 ? (
                        <span className="text-[10px] font-black text-purple-500 bg-purple-500/10 px-2 py-1 rounded-lg">
                          ₦{tx.cashback_earned.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold text-muted-gray opacity-30">₦0.00</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${getStatusStyle(tx.status)}`}>
                        {Icon && <HugeiconsIcon icon={Icon} size={12} />}
                        {tx.status}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filteredTransactions.length === 0 && (
            <div className="p-8 text-center text-muted-gray text-sm">
                No transactions found matching your filters.
            </div>
        )}
        
        {/* Pagination Controls */}
        {pagination.last_page > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-light-gray dark:border-dark-border bg-gray-50/50 dark:bg-gray-800/30">
            <span className="text-xs text-muted-gray font-medium">
              Page {pagination.current_page} of {pagination.last_page} ({pagination.total} records)
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-xs font-bold rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 hover:bg-white dark:hover:bg-gray-700 transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => Math.min(pagination.last_page, p + 1))}
                disabled={page === pagination.last_page}
                className="px-3 py-1.5 text-xs font-bold rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 hover:bg-white dark:hover:bg-gray-700 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default History;